import React from 'react';
import { Link } from 'react-router-dom';
import { HeroSection } from './HeroSection';
import { ProblemSection } from './ProblemSection';
import { SolutionSection } from './SolutionSection';
import { DemoSection } from './DemoSection';
import styles from './LandingPage.module.scss';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';
import { usePageSEO } from '../../hooks/usePageSEO';
import { TopNavigation } from '../Navigation/TopNavigation';
import { APP_YEAR } from '../../utils/constants';
import { tokens } from '../../styles/tokens';
import { CONTACT_EMAIL, CONTACT_MAILTO } from '../../utils/contactInfo';

const footerHeading = { fontSize: '14px', marginBottom: '8px', color: tokens.inkBlack } as const;
const footerCta = { color: tokens.softRed, fontWeight: 600 } as const;

const RevealSection: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => {
  const { elementRef, isVisible } = useIntersectionObserver({ threshold: 0.1 });
  return (
    <div 
      ref={elementRef} 
      className={`${styles.revealSection} ${isVisible ? styles.visible : ''} ${className || ''}`}
    >
      {children}
    </div>
  );
};

export const LandingPage: React.FC = () => {
  usePageSEO({
    title: "MyMinu - Visual Routines for Kids",
    description: "Turn daily chaos into calm. The visual routine manager that helps kids understand time and build independence.",
    image: window.location.origin + "/logo512.png"
  });

  return (
    <div className={styles.landingContainer}>
      <TopNavigation />

      <main>
        <HeroSection />
        <ProblemSection />
        <SolutionSection />
        <DemoSection />

        {/* --- MIGRATED FROM LOGIN SCREEN --- */}
        
        <div className={styles.contentSection}>
          {/* Why It Matters (Science/Education) */}
          <RevealSection className={styles.sectionBlock}>
              <h2 className={styles.sectionTitle}>Why Visual Time Matters</h2>
              <p className={styles.sectionSubtitle}>
                  Abstract time is hard for young minds. MyMinu turns "10 minutes left" into a visual slice of pie, making time tangible and manageable.
              </p>
              
              <div className={styles.bentoGrid}>
                  <div className={`${styles.bentoCard} ${styles.cardLarge}`}>
                      <span className={styles.icon}>🧠</span>
                      <h3>Cognitive Development</h3>
                      <p>Young children lack the cognitive framework for abstract time. Visual timers bridge the gap between "now" and "later" by using spatial representation.</p>
                  </div>
                  <div className={styles.bentoCard}>
                      <span className={styles.icon}>😌</span>
                      <h3>Reduce Anxiety</h3>
                      <p>Knowing exactly "how much longer" reduces transition anxiety and tantrums.</p>
                  </div>
                  <div className={styles.bentoCard}>
                      <span className={styles.icon}>🎯</span>
                      <h3>Build Focus</h3>
                      <p>Visual boundaries help maintain attention on the current task without constant verbal reminders.</p>
                  </div>
              </div>
          </RevealSection>

          {/* Use Cases */}
          <RevealSection className={styles.sectionBlock}>
              <h2 className={styles.sectionTitle}>Perfect For Every Setting</h2>
              
              <div className={styles.useCaseContainer}>
                  <div className={styles.useCaseRow}>
                      <div className={styles.useCaseContent}>
                          <h3>🏠 For Families</h3>
                          <ul>
                              <li>Make morning routines chaos-free.</li>
                              <li>Visualize homework and screen time limits.</li>
                              <li>Build independence—kids start the clock themselves!</li>
                              <li>Sync across devices so Mom, Dad, and Kids are on the same schedule.</li>
                          </ul>
                      </div>
                      <div className={styles.useCaseVisual}>
                         <span className={styles.largeEmoji}>👨‍👩‍👧‍👦</span>
                      </div>
                  </div>

                  <div className={`${styles.useCaseRow} ${styles.reverse}`}>
                      <div className={styles.useCaseContent}>
                          <h3>🏫 For Kindergartens & Schools</h3>
                          <ul>
                              <li>Manage classroom transitions smoothly.</li>
                              <li>Visual cues for clean-up time, circle time, and quiet reading.</li>
                              <li>Help special needs students anticipate changes.</li>
                              <li>Gamify classroom chores to build community responsibility.</li>
                          </ul>
                      </div>
                      <div className={styles.useCaseVisual}>
                         <span className={styles.largeEmoji}>🎒</span>
                      </div>
                  </div>

                   <div className={styles.useCaseRow}>
                      <div className={styles.useCaseContent}>
                          <h3>🧩 For Neurodiverse Minds</h3>
                          <ul>
                              <li>ADHD-friendly visual structure reducing time blindness.</li>
                              <li>Autism support through predictable, visual routine blocks.</li>
                              <li>Low-stimulation mode for sensory-sensitive users.</li>
                              <li>Positive reinforcement for task completion.</li>
                          </ul>
                      </div>
                      <div className={styles.useCaseVisual}>
                         <span className={styles.largeEmoji}>🧠</span>
                      </div>
                  </div>
              </div>
          </RevealSection>

          {/* How it Works (Simplified) */}
          <RevealSection className={styles.howItWorks}>
            <h2 className={styles.sectionTitle}>Get Started in Seconds</h2>
            <div className={styles.stepsContainer}>
              <div className={styles.step}>
                <div className={styles.stepNumber}>1</div>
                <h4>Set the Routine</h4>
                <p>Drag and drop tasks to create your perfect timeline.</p>
              </div>
              <div className={styles.step}>
                <div className={styles.stepNumber}>2</div>
                <h4>Visual Countdown</h4>
                <p>The clock face fills up, showing time passing visually.</p>
              </div>
              <div className={styles.step}>
                <div className={styles.stepNumber}>3</div>
                <h4>Celebrate Wins</h4>
                <p>Complete tasks on time to earn stars and build streaks!</p>
              </div>
            </div>
          </RevealSection>
          
          {/* CTA */}
          <RevealSection className={styles.ctaSection}>
              <h2>Ready to Master Time?</h2>
              <p>Join thousands of families and educators building better habits today.</p>
              <div className={styles.ctaButtons}>
                  <Link to="/register" className={styles.primaryCta}>Get Started for Free</Link>
              </div>
          </RevealSection>
        </div>

      </main>

      <footer className={styles.footer}>
        <div style={{ marginBottom: '24px', display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '24px' }}>
          <div>
            <h4 style={footerHeading}>Product</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Link to="/download">Download</Link>
              <Link to="/pricing">Pricing</Link>
              <Link to="/science-of-time" style={footerCta}>Science of Time</Link>
            </div>
          </div>
          <div>
            <h4 style={footerHeading}>Company</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Link to="/about">About Us</Link>
              <Link to="/careers">Careers</Link>
              <Link to="/blog">Blog</Link>
              <a href={CONTACT_MAILTO}>{CONTACT_EMAIL}</a>
            </div>
          </div>
          <div>
            <h4 style={footerHeading}>Legal</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Link to="/legal/privacy">Privacy Policy</Link>
              <Link to="/legal/terms">Terms of Service</Link>
              <Link to="/legal/impressum">Impressum</Link>
              <Link to="/legal/cookies">Cookie Settings</Link>
            </div>
          </div>
        </div>
        <p>© {APP_YEAR} MyMinu. All rights reserved.</p>
      </footer>
    </div>
  );
};
